import { useAuthStore } from '@/auth/authStore';
import { SERVER_URL, COMMON_HEADERS } from '@/config/serverUrl';
import type { CosmeticSlot } from '@/auth/authApi';

const BASE = SERVER_URL;

export type Rarity = 'common' | 'rare' | 'epic' | 'legendary';

export interface CosmeticItem {
  id: string;
  slot: CosmeticSlot;
  name: string;
  description: string | null;
  rarity: Rarity;
  priceCoins: number;
  priceGems: number;
  iconUrl: string | null;
  data: Record<string, unknown>;
  isActive: boolean;
  isStarter: boolean;
  /** null/undefined = permanently owned or never trialed; >0 = active temporary use. */
  trialUsesLeft?: number | null;
}

export interface InventoryResponse {
  owned: CosmeticItem[];
  equipped: Record<CosmeticSlot, string | null>;
}

export interface ShopBundle {
  id: string;
  name: string;
  description: string | null;
  rarity: Rarity;
  priceCoins: number;
  priceGems: number;
  bannerUrl: string | null;
  owned: boolean;
  character: {
    id: string;
    name: string;
    idleUrl: string | null;
    climbUrl: string | null;
    fallUrl: string | null;
    winUrl: string | null;
    idleFrames: string[] | null;
    climbFrames: string[] | null;
    fallFrames: string[] | null;
    winFrames: string[] | null;
  };
  avatarFrame: {
    id: string;
    name: string;
    iconUrl: string | null;
    data: Record<string, unknown>;
  };
  finisher: {
    id: string;
    name: string;
    iconUrl: string | null;
    data: Record<string, unknown>;
  };
}

export class ShopApiError extends Error {
  constructor(public code: string, message: string, public status: number) {
    super(message);
  }
}

async function req<T>(method: string, path: string, body?: unknown): Promise<T> {
  const token = useAuthStore.getState().token;
  const res = await fetch(`${BASE}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...COMMON_HEADERS,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body == null ? undefined : JSON.stringify(body),
    credentials: 'include',
  });
  if (!res.ok) {
    let code = `HTTP_${res.status}`;
    let message = res.statusText;
    try {
      const j = (await res.json()) as { error?: string; message?: string };
      if (j.error) code = j.error;
      if (j.message) message = j.message;
    } catch {
      /* */
    }
    throw new ShopApiError(code, message, res.status);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const shopApi = {
  catalog: () => req<{ items: CosmeticItem[] }>('GET', '/shop/catalog'),
  bundles: () => req<{ items: ShopBundle[] }>('GET', '/shop/bundles'),
  buyBundle: (bundleId: string) =>
    req<{ coins: number; gems: number; bundleId: string }>(
      'POST',
      `/shop/bundles/${bundleId}/buy`,
    ),
  buy: (cosmeticId: string) =>
    req<{ coins: number; gems: number; cosmeticId: string }>('POST', `/shop/buy/${cosmeticId}`),
  inventory: () => req<InventoryResponse>('GET', '/inventory/'),
  equip: (cosmeticId: string) =>
    req<{ ok: true; slot: CosmeticSlot }>('POST', `/inventory/equip/${cosmeticId}`),
  unequip: (slot: CosmeticSlot) =>
    req<{ ok: true; slot: CosmeticSlot }>('POST', `/inventory/unequip/${slot}`),
};

export const SLOT_LABEL: Record<CosmeticSlot, string> = {
  hat: 'Hat',
  body: 'Outfit',
  cape: 'Cape',
  victoryShout: 'Victory Shout',
  avatarFrame: 'Avatar Frame',
  endgameFx: 'Finisher',
  matchBackground: 'Match Background',
};

export const RARITY_COLOR: Record<Rarity, string> = {
  common: 'rgba(245,243,255,0.5)',
  rare: 'var(--c-blue)',
  epic: 'var(--c-purple)',
  legendary: 'var(--c-yellow)',
};

// victoryShout removed from the shop — it's now a free-text field players set
// in Settings. avatarFrame is purchasable; when nothing is equipped the server
// falls back to the player's rank-tier frame.
export const SLOT_ORDER: CosmeticSlot[] = ['hat', 'body', 'cape', 'avatarFrame'];
